import { FC, useState } from 'react'

import { GRAY_400 } from 'constants/styling/theme'
import { Nullable } from 'models/helpers'
import Rating from '@mui/material/Rating'
import Stack from '@mui/material/Stack'
import StarIcon from '@mui/icons-material/Star'
import { StarRatingValue } from 'constants/misc'
import { getStarColor } from '../getStarColor'

interface Props {
  /** Current rating value (null if not yet selected) */
  value: Nullable<StarRatingValue>
  /** Called when the user selects a new rating */
  onChange: (value: StarRatingValue) => void
  /** If true, stars are not interactive */
  readOnly?: boolean
  /** MUI Rating size */
  size?: 'small' | 'medium' | 'large'
}

/**
 * 5-star rating input with whole-number precision (1-5).
 * Stars appear inside a full-width bordered box.
 * Color changes based on rating: green (5), yellow (3-4), red (<=2).
 * Hover previews the color for the hovered value.
 *
 * @example
 * <StarRatingInput value={3} onChange={(val) => setRating(val)} />
 */
export const StarRatingInput: FC<Props> = ({
  value,
  onChange,
  readOnly = false,
  size = 'large'
}) => {
  const [hoverValue, setHoverValue] = useState<number>(-1)

  // Use hovered value for color when hovering, otherwise the committed value
  const displayValue = hoverValue !== -1 ? hoverValue : value
  const activeColor = getStarColor(displayValue)
  const fontSize = size === 'large' ? '4.8rem' : undefined

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '.8rem',
        padding: '2rem 1.2rem',
        width: '100%'
      }}
    >
      <Rating
        value={value}
        onChange={(_, newValue) => {
          if (newValue) onChange(newValue as StarRatingValue)
        }}
        onChangeActive={(_, newHover) => setHoverValue(newHover)}
        precision={1}
        size={size}
        readOnly={readOnly}
        emptyIcon={<StarIcon fontSize="inherit" />}
        sx={{
          fontSize,
          '& .MuiRating-iconEmpty': {
            color: GRAY_400
          },
          '& .MuiRating-iconFilled': {
            color: activeColor
          },
          '& .MuiRating-iconHover': {
            transform: 'scale(1.15)'
          },
          '& .MuiRating-iconActive': {
            transform: 'scale(1.2)'
          }
        }}
      />
    </Stack>
  )
}
